"use client";
import type { WhisperJson } from "@teksta/shared-types";
import Lunr from "lunr";
import { useMemo, useState } from "react";

export const useSearchSegments = ({
  lunrIndex,
  whisperJson,
}: {
  lunrIndex: object | null | undefined;
  whisperJson: WhisperJson;
}) => {
  const index = useMemo(() => (lunrIndex ? Lunr.Index.load(lunrIndex) : null), [lunrIndex]);

  const nGrammer = (string: string) => generateNGrams(string, 2, 3);

  const [queryState, setQueryState] = useState("");

  const handleSearch = ({ query }: { query: string }) => {
    setQueryState(query.toLowerCase());
  };

  const { resultsIndexes, queryTime, isSearched, results } = useMemo(() => {
    const query = queryState;
    const startTime = Date.now();

    if (!query) {
      return {
        isSearched: false,
        results: [],
        resultsIndexes: [],
        queryTime: 0,
      };
    }
    const processedQuery = nGrammer(query).join(" ");
    let hits: LunrHit[] = index?.search(processedQuery) ?? [];
    const docs: SearchResult[] = [];
    const indexes: number[] = [];
    let scoreCutoff = 1;
    while (hits && hits.length > 50) {
      hits = hits.filter(({ score }) => score > scoreCutoff);
      scoreCutoff += 1;
    }
    hits.forEach((hit) => {
      if (whisperJson.segments[Number.parseInt(hit.ref, 10)].text.toLowerCase().includes(query)) {
        hit.score += 10;
      }
    });
    hits.sort((a, b) => b.score - a.score);
    hits.slice(0, 6).forEach(({ ref, score }) => {
      const segmentIndex = Number.parseInt(ref, 10);
      const segment = whisperJson.segments[segmentIndex];
      const highlights: {
        startIndex: number;
        endIndex: number;
      }[] = [];
      let highlightText = segment.text.toLowerCase();
      while (true) {
        const startIndex = highlightText.indexOf(query.toLowerCase());
        if (startIndex === -1) {
          break;
        }
        const endIndex = startIndex + query.length;
        highlights.push({ startIndex, endIndex });
        highlightText = highlightText.slice(endIndex);
      }

      indexes.push(segmentIndex);
      docs.push({
        highlights,
        ref,
        score,
        ...segment,
      });
    });

    const endTime = Date.now() - startTime;

    return {
      results: docs,
      resultsIndexes: indexes,
      queryTime: endTime,
      isSearched: true,
    };
  }, [queryState, index, whisperJson]);

  if (!index) {
    return { isSearched: false, executeSearch: handleSearch };
  }
  return {
    queryTime,
    results,
    isSearched,
    executeSearch: handleSearch,
    resultsIndexes,
    query: queryState,
  };
};

function generateNGrams(text: string, minGram: number, maxGram: number) {
  if (text.length < minGram) {
    return [];
  }
  const nGrams = [];

  // Ensure that minGram and maxGram are valid
  if (minGram <= 0 || maxGram < minGram) {
    throw new Error("Invalid ngrams");
  }

  // Generate n-grams for each length from minGram to maxGram
  for (let n = minGram; n <= maxGram; n++) {
    for (let i = 0; i <= text.length - n; i++) {
      nGrams.push(text.substring(i, i + n));
    }
  }

  return nGrams;
}
